/**
 * BIC validation, mirroring BicUtils on the backend.
 *
 * Kept in step with
 *   src/ZeitonBackend.API/Features/Finance/Invoices/TransferLists/Utils/BicUtils.cs
 * so the export grid rejects the same values the server would.
 */

/**
 * What the export grid holds when a recipient has no BIC. It has always meant "no creditor agent",
 * so it is treated exactly like a blank value.
 */
export const BIC_NOT_PROVIDED = '00000000';

/** BICFIDec2014Identifier from the pain.001.001.09 schema: 8 or 11 characters. */
const BIC_STRUCTURE = /^[A-Z0-9]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/**
 * Strips separators and upper-cases. Non-ASCII characters are kept, so a BIC containing one still
 * fails the structure check rather than being silently shortened into a valid-looking one.
 */
export function normalizeBic(value: string | null | undefined): string {
    if (!value) return '';

    return value.replace(/[^0-9a-zA-Z\u0080-￿]/g, '').toUpperCase();
}

/** Whether a BIC was entered at all, as opposed to a blank or the "no BIC" placeholder. */
export function isBicProvided(value: string | null | undefined): boolean {
    const bic = normalizeBic(value);

    return bic.length > 0 && bic !== BIC_NOT_PROVIDED;
}

export function isValidBic(value: string | null | undefined): boolean {
    return BIC_STRUCTURE.test(normalizeBic(value));
}
